import {
  MessageTemplateType,
  messageTemplateTypeDescriptions,
  noVariablesMessageTemplateTypes,
} from "@repo/app-types/entities/messageTemplate";
import { Input } from "@repo/ui/Inputs/input";
import { Textarea } from "@repo/ui/Inputs/textarea";
import type { FC } from "react";
import { useMessageVariables } from "./useMessageTemplateVariables.js";

// Maximum length for Outreach message body - equivalent to two SMS messages
// Restricting since we want to be careful about running into spam issues.
const MAX_OUTREACH_BODY_LENGTH = 320;

export interface MessageTemplateFormProps {
  subject: string;
  body: string;
  type: MessageTemplateType;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
}

const getSubjectPlaceholder = (type: MessageTemplateType): string => {
  switch (type) {
    case MessageTemplateType.Advanced:
      return "Next steps for your application";
    case MessageTemplateType.Outreach:
      return "You're a great fit for this role";
    case MessageTemplateType.RecordingSummary:
      return "Discovery call template";
    case MessageTemplateType.RecordingEmail:
      return "Lunch meeting follow up email template";
    case MessageTemplateType.Rejected:
    case MessageTemplateType.NextMeetingInvite:
    default:
      return "An update on your application";
  }
};

export const MessageTemplateForm: FC<MessageTemplateFormProps> = ({
  subject,
  body,
  type,
  onSubjectChange,
  onBodyChange,
}) => {
  // Some template types do not support variables
  const showAddVariableButtons =
    !noVariablesMessageTemplateTypes.includes(type);

  const { ref: subjectRef, widget: subjectVariablesWidget } =
    useMessageVariables<HTMLInputElement>({
      type,
      value: subject,
      onChange: onSubjectChange,
    });

  const { ref: bodyRef, widget: bodyVariablesWidget } =
    useMessageVariables<HTMLTextAreaElement>({
      type,
      value: body,
      onChange: onBodyChange,
    });

  return (
    <div className="w-[700px] space-y-4">
      <div>
        <Input
          ref={subjectRef}
          autoFocus
          label={messageTemplateTypeDescriptions[type].subjectLabel}
          placeholder={getSubjectPlaceholder(type)}
          description={messageTemplateTypeDescriptions[type].subject}
          value={subject}
          onChange={(e) => onSubjectChange(e.target.value)}
          maxLength={100}
        />
        {showAddVariableButtons && (
          <div className="mt-2">{subjectVariablesWidget}</div>
        )}
      </div>
      <div>
        <Textarea
          ref={bodyRef}
          label="Body"
          description={messageTemplateTypeDescriptions[type].body}
          placeholder={messageTemplateTypeDescriptions[type].bodyPlaceholder}
          value={body}
          onChange={onBodyChange}
          height="200px"
          maxLength={
            type === MessageTemplateType.Outreach
              ? MAX_OUTREACH_BODY_LENGTH
              : undefined
          }
        />
        {showAddVariableButtons && (
          <div className="mt-2">{bodyVariablesWidget}</div>
        )}
      </div>
    </div>
  );
};
